import React, { useState, useEffect } from "react";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { useContacts } from "src/features/contacts/hooks/useContacts";

export function BuyerRequirementForm({
  requirement,
  onSubmit,
  onCancel,
  isPending,
}) {
  const [formData, setFormData] = useState({
    contact_id: "",
    status: "active",
    city: "",
    min_budget: "",
    max_budget: "",
    min_bedrooms: "",
    max_bedrooms: "",
    min_area_sqft: "",
    urgency: "exploring",
  });

  const { data: contactsData, fetch: fetchContacts } = useContacts();
  const contacts = contactsData.contacts || [];

  useEffect(() => {
    // Load contacts so the requirement can be linked to its buyer.
    fetchContacts({ per_page: 100 });
  }, [fetchContacts]);

  useEffect(() => {
    if (requirement) {
      setFormData({
        contact_id: requirement.contact?.id
          ? String(requirement.contact.id)
          : "",
        status: requirement.status || "active",
        city: requirement.city || "",
        min_budget: requirement.min_budget || "",
        max_budget: requirement.max_budget || "",
        min_bedrooms: requirement.min_bedrooms || "",
        max_bedrooms: requirement.max_bedrooms || "",
        min_area_sqft: requirement.min_area_sqft || "",
        urgency: requirement.urgency || "exploring",
      });
    }
  }, [requirement]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const [contactError, setContactError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.contact_id) {
      setContactError("Please select a contact for this requirement.");
      return;
    }
    setContactError("");

    onSubmit(formData);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col h-full overflow-hidden"
    >
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="contact_id">Contact *</Label>
            <Select
              value={formData.contact_id}
              onValueChange={(val) => {
                handleSelectChange("contact_id", val);
                setContactError("");
              }}
            >
              <SelectTrigger id="contact_id">
                <SelectValue placeholder="Select a contact" />
              </SelectTrigger>
              <SelectContent>
                {contacts.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-slate-400">
                    No contacts available
                  </div>
                ) : (
                  contacts.map((contact) => (
                    <SelectItem key={contact.id} value={String(contact.id)}>
                      {contact.name}
                      {contact.phone ? ` · ${contact.phone}` : ""}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {contactError && (
              <p className="mt-1 text-sm text-red-600">{contactError}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Status</Label>
              <Select
                value={formData.status}
                onValueChange={(val) => handleSelectChange("status", val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="fulfilled">Fulfilled</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Urgency</Label>
              <Select
                value={formData.urgency}
                onValueChange={(val) => handleSelectChange("urgency", val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Urgency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immediate</SelectItem>
                  <SelectItem value="1_3_months">1-3 Months</SelectItem>
                  <SelectItem value="3_6_months">3-6 Months</SelectItem>
                  <SelectItem value="exploring">Just Exploring</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="city">Preferred City</Label>
            <Input
              id="city"
              name="city"
              placeholder="e.g. Bangalore"
              value={formData.city}
              onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="min_budget">Min Budget (₹)</Label>
              <Input
                id="min_budget"
                name="min_budget"
                type="number"
                value={formData.min_budget}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="max_budget">Max Budget (₹)</Label>
              <Input
                id="max_budget"
                name="max_budget"
                type="number"
                value={formData.max_budget}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="min_bedrooms">Min Bedrooms</Label>
              <Input
                id="min_bedrooms"
                name="min_bedrooms"
                type="number"
                value={formData.min_bedrooms}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="max_bedrooms">Max Bedrooms</Label>
              <Input
                id="max_bedrooms"
                name="max_bedrooms"
                type="number"
                value={formData.max_bedrooms}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 p-6 bg-slate-50 border-t border-slate-100">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? "Saving..." : requirement ? "Update Requirement" : "Create Requirement"}
        </Button>
      </div>
    </form>
  );
}
